import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import HeaderComponent from "@components/common/Header/Header";
import { TextField, Select, MenuItem, FormControl } from "@mui/material";

const COMMODITY_TYPES = [
  "Agricultural Products",
  "Metals",
  "Energy",
  "Livestock",
  "Softs",
  "Other",
];

const ROLE_DESIGNATIONS = [
  "CEO",
  "CFO",
  "Director",
  "Manager",
  "Administrator",
  "Supervisor",
  "Team Lead",
  "Coordinator",
  "Other",
];

const EditMyAccount = () => {
  const formik = useFormik({
    initialValues: {
      // Primary Details
      name: "",
      email: "",
      contactNumber: "",

      // Company Details
      registeredCompanyName: "",
      tradingAs: "",
      commodityType: "",

      // Administrative Contact Details
      firstName: "",
      lastName: "",
      primaryEmail: "",
      primaryContactNumber: "",
      roleDesignation: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      contactNumber: Yup.string().required("Contact number is required"),
      registeredCompanyName: Yup.string().required("Company name is required"),
      tradingAs: Yup.string().required("Trading name is required"),
      commodityType: Yup.string().required("Commodity type is required"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      primaryEmail: Yup.string()
        .email("Invalid email")
        .required("Primary email is required"),
      primaryContactNumber: Yup.string().required(
        "Primary contact number is required",
      ),
      roleDesignation: Yup.string().required("Role/Designation is required"),
    }),
    onSubmit: (values) => {
      console.log(values);
      // Handle form submission
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="tw-bg-gray-50 tw-font-sans">
      {/* Header Section */}
      <HeaderComponent />
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a href="/my-account">
                <img src={BackIcon} alt="Back Icon" />{" "}
              </a>
              <h1 className="tw-text-xl tw-ml-4 tw-font-semibold font-primary-color">
                Edit My Account Details
              </h1>
            </div>
          </div>
        </div>
      </div>
      {/* Content Section */}
      <div className="tw-px-8 tw-py-4 tw-mt-[140px]">
        <div className="tw-bg-white custom-card">
          {/* My Account Details */}
          <div>
            <div className="custom-card-header">
              <h3 className="font-size-base tw-font-semibold font-primary-color">
                Primary Details
              </h3>
            </div>
            <div className="custom-card-body">
              <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Name
                  </p>
                  <TextField
                    fullWidth
                    type="text"
                    name="name"
                    variant="outlined"
                    placeholder="Enter Name"
                    className="tw-mb-4"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Email
                  </p>
                  <TextField
                    fullWidth
                    type="email"
                    name="email"
                    variant="outlined"
                    placeholder="Enter Email"
                    className="tw-mb-4"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Contact Number
                  </p>
                  <TextField
                    fullWidth
                    type="number"
                    name="contactNumber"
                    variant="outlined"
                    placeholder="Enter Contact Number"
                    className="tw-mb-4"
                    value={formik.values.contactNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.contactNumber &&
                      Boolean(formik.errors.contactNumber)
                    }
                    helperText={
                      formik.touched.contactNumber &&
                      formik.errors.contactNumber
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-bg-white custom-card">
          {/* My Account Details */}
          <div>
            <div className="custom-card-header">
              <h3 className="font-size-base tw-font-semibold font-primary-color">
                Company Details
              </h3>
            </div>
            <div className="custom-card-body">
              <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Registered Company Name
                  </p>
                  <TextField
                    fullWidth
                    type="text"
                    name="registeredCompanyName"
                    variant="outlined"
                    placeholder="Enter Registered Company Name"
                    className="tw-mb-4"
                    value={formik.values.registeredCompanyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.registeredCompanyName &&
                      Boolean(formik.errors.registeredCompanyName)
                    }
                    helperText={
                      formik.touched.registeredCompanyName &&
                      formik.errors.registeredCompanyName
                    }
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Trading As
                  </p>
                  <TextField
                    fullWidth
                    type="text"
                    name="tradingAs"
                    variant="outlined"
                    placeholder="Enter Trading As"
                    className="tw-mb-4"
                    value={formik.values.tradingAs}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.tradingAs &&
                      Boolean(formik.errors.tradingAs)
                    }
                    helperText={
                      formik.touched.tradingAs && formik.errors.tradingAs
                    }
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Commodity Type
                  </p>
                  <FormControl fullWidth className="tw-mb-4">
                    <Select
                      id="commodityType"
                      name="commodityType"
                      value={formik.values.commodityType}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.commodityType &&
                        Boolean(formik.errors.commodityType)
                      }
                      displayEmpty
                      placeholder="Select Commodity Type"
                    >
                      <MenuItem value="" disabled>
                        <span className="tw-text-gray-400">
                          Select Commodity Type
                        </span>
                      </MenuItem>
                      {COMMODITY_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.commodityType &&
                      formik.errors.commodityType && (
                        <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                          {formik.errors.commodityType}
                        </div>
                      )}
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-bg-white custom-card">
          {/* My Account Details */}
          <div>
            <div className="custom-card-header">
              <h3 className="font-size-base tw-font-semibold font-primary-color">
                Administrative Contact Details
              </h3>
            </div>
            <div className="custom-card-body">
              <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    First Name
                  </p>
                  <TextField
                    fullWidth
                    type="text"
                    name="firstName"
                    variant="outlined"
                    placeholder="Enter First Name"
                    className="tw-mb-4"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    helperText={
                      formik.touched.firstName && formik.errors.firstName
                    }
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Last Name
                  </p>
                  <TextField
                    fullWidth
                    type="text"
                    name="lastName"
                    variant="outlined"
                    placeholder="Enter Last Name"
                    className="tw-mb-4"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    helperText={
                      formik.touched.lastName && formik.errors.lastName
                    }
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Primary Email
                  </p>
                  <TextField
                    fullWidth
                    type="email"
                    name="primaryEmail"
                    variant="outlined"
                    placeholder="Enter Primary Email"
                    className="tw-mb-4"
                    value={formik.values.primaryEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.primaryEmail &&
                      Boolean(formik.errors.primaryEmail)
                    }
                    helperText={
                      formik.touched.primaryEmail && formik.errors.primaryEmail
                    }
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Primary Contact Number
                  </p>
                  <TextField
                    fullWidth
                    type="number"
                    name="primaryContactNumber"
                    variant="outlined"
                    placeholder="Enter Primary Contact Number"
                    className="tw-mb-4"
                    value={formik.values.primaryContactNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.primaryContactNumber &&
                      Boolean(formik.errors.primaryContactNumber)
                    }
                    helperText={
                      formik.touched.primaryContactNumber &&
                      formik.errors.primaryContactNumber
                    }
                  />
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Role/Designation
                  </p>
                  <FormControl fullWidth className="tw-mb-4">
                    <Select
                      id="roleDesignation"
                      name="roleDesignation"
                      value={formik.values.roleDesignation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.roleDesignation &&
                        Boolean(formik.errors.roleDesignation)
                      }
                      displayEmpty
                      placeholder="Select Role/Designation"
                    >
                      <MenuItem value="" disabled>
                        <span className="tw-text-gray-400">
                          Select Role/Designation
                        </span>
                      </MenuItem>
                      {ROLE_DESIGNATIONS.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.roleDesignation &&
                      formik.errors.roleDesignation && (
                        <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                          {formik.errors.roleDesignation}
                        </div>
                      )}
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-flex tw-justify-center tw-items-center">
          <button
            type="submit"
            className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
          >
            Save
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditMyAccount;
